import React, { useCallback, useRef, useState } from 'react';
import { Check, Eye, Loader2, Pencil, Redo2, Save, Undo2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from './markdown';
import HighlightEditPopover from './HighlightEditPopover';
import { clampPopover, isUsableSelection, occurrenceBeforeOffset } from './selection';
import type { SelectionInfo } from './types';

interface Props {
  draftMarkdown: string;
  draftVersion: number;
  isEditing: boolean;
  isSaving: boolean;
  createdSurveyId: number | null;
  canUndo: boolean;
  canRedo: boolean;
  showEditedFlash: boolean;
  onApplyEdit: (params: { instruction: string; selectedText: string; occurrence: number }) => void;
  onManualChange: (markdown: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onSave: (payload: { name?: string }) => void;
  onViewSurvey: (surveyId: number) => void;
}

const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT = 130;

const SurveyCanvas: React.FC<Props> = ({
  draftMarkdown,
  draftVersion,
  isEditing,
  isSaving,
  createdSurveyId,
  canUndo,
  canRedo,
  showEditedFlash,
  onApplyEdit,
  onManualChange,
  onUndo,
  onRedo,
  onSave,
  onViewSurvey,
}) => {
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [selection, setSelection] = useState<SelectionInfo | null>(null);
  const [draftBuffer, setDraftBuffer] = useState(draftMarkdown);
  const [showSaveBar, setShowSaveBar] = useState(false);
  const [saveName, setSaveName] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  // Keep the edit-mode textarea buffer in sync when the draft changes from outside (agent edits).
  // Also drop any open/stale selection: when the draft changes (history load, agent edit, undo/redo,
  // an applied edit) the popover's captured selectedText/offsets no longer match what's rendered,
  // so submitting it would send the wrong span ("could not locate the selected text").
  React.useEffect(() => {
    setDraftBuffer(draftMarkdown);
    setSelection(null);
  }, [draftMarkdown]);

  const handleMouseUp = useCallback(() => {
    if (mode !== 'preview') return;
    const sel = window.getSelection();
    const container = contentRef.current;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !container) {
      setSelection(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) {
      setSelection(null);
      return;
    }
    const selectedText = sel.toString();
    if (!isUsableSelection(selectedText)) {
      setSelection(null);
      return;
    }
    // Character offset of the selection start within the container's rendered text.
    const pre = range.cloneRange();
    pre.selectNodeContents(container);
    pre.setEnd(range.startContainer, range.startOffset);
    const offset = pre.toString().length + selectedText.length;
    // Occurrence is counted in the RENDERED text; the backend resolves it against the RAW
    // markdown. For a phrase that appears once (the common case) this is exact; for a phrase
    // duplicated across markdown-decorated and plain contexts it's best-effort, and the edit
    // is still span-bounded server-side. (Improving duplicate disambiguation is a fast-follow.)
    const occurrence = occurrenceBeforeOffset(container.textContent || '', offset, selectedText);

    const rect = range.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    const { top, left } = clampPopover(
      rect.bottom - cRect.top + container.scrollTop + 8,
      rect.left - cRect.left,
      container.clientWidth,
      container.scrollHeight,
      POPOVER_WIDTH,
      POPOVER_HEIGHT,
    );
    setSelection({ selectedText, occurrence, top, left });
  }, [mode]);

  // Tracks an in-flight highlight edit so we can keep the popover open (showing its busy spinner)
  // until the edit resolves, then close it — instead of closing the instant the request fires.
  const submittedRef = useRef(false);

  const submitEdit = (instruction: string) => {
    if (!selection) return;
    submittedRef.current = true;
    onApplyEdit({ instruction, selectedText: selection.selectedText, occurrence: selection.occurrence });
    // Keep the selection/popover mounted so the user sees the "Applying…" state.
  };

  // Close the popover once a submitted edit finishes (success or error). On success the draft
  // changes and the [draftMarkdown] effect has already cleared the selection; this also covers the
  // error path, where the draft is unchanged.
  React.useEffect(() => {
    if (!isEditing && submittedRef.current) {
      submittedRef.current = false;
      setSelection(null);
      window.getSelection()?.removeAllRanges();
    }
  }, [isEditing]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Canvas header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#F1F5F9]">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#1C1C1E]">Survey draft</span>
          {draftVersion > 0 && (
            <span className="text-[10px] text-gray-400 bg-[#FAFBFC] border border-[#E5E7EB] rounded-full px-2 py-0.5">
              v{draftVersion}
            </span>
          )}
          {showEditedFlash && (
            <span className="text-[10px] text-[#07E5D1] flex items-center gap-1">
              <Check className="w-3 h-3" /> Updated
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-[#FAFBFC] disabled:opacity-30"
            title="Undo"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-[#FAFBFC] disabled:opacity-30"
            title="Redo"
          >
            <Redo2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              // Drop any active selection: its coordinates/offsets become stale across the
              // preview↔edit switch, so a returning popover would render in the wrong place.
              setSelection(null);
              setMode((m) => (m === 'preview' ? 'edit' : 'preview'));
            }}
            className="ml-1 inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-[#1C1C1E] border border-[#E5E7EB] rounded-lg px-2.5 py-1.5"
          >
            {mode === 'preview' ? <Pencil className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {mode === 'preview' ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>

      {/* Canvas body */}
      <div className="flex-1 overflow-y-auto relative">
        {mode === 'preview' ? (
          <div
            ref={contentRef}
            onMouseUp={handleMouseUp}
            className="px-6 py-5 prose-survey selection:bg-[#07E5D1]/20"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {draftMarkdown}
            </ReactMarkdown>
            {selection && (
              <HighlightEditPopover
                top={selection.top}
                left={selection.left}
                selectedText={selection.selectedText}
                isBusy={isEditing}
                onSubmit={submitEdit}
                onClose={() => setSelection(null)}
              />
            )}
          </div>
        ) : (
          <textarea
            value={draftBuffer}
            onChange={(e) => setDraftBuffer(e.target.value)}
            onBlur={() => onManualChange(draftBuffer)}
            className="w-full h-full min-h-[400px] px-6 py-5 text-sm font-mono text-[#374151] resize-none focus:outline-none"
            spellCheck={false}
          />
        )}
        {isEditing && (
          <div className="absolute top-2 right-4 text-[11px] text-[#07E5D1] flex items-center gap-1 bg-white/90 rounded-full px-2 py-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Applying edit…
          </div>
        )}
      </div>

      {/* Canvas footer / save */}
      <div className="border-t border-[#F1F5F9] px-4 py-3">
        {createdSurveyId ? (
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#10B981] flex items-center gap-1.5">
              <Check className="w-4 h-4" /> Survey created
            </span>
            <button
              onClick={() => onViewSurvey(createdSurveyId)}
              className="text-xs font-medium text-white bg-[#07E5D1] hover:brightness-95 rounded-lg px-3 py-1.5"
            >
              View survey
            </button>
          </div>
        ) : showSaveBar ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="Survey name"
                disabled={isSaving}
                className="flex-1 text-sm border border-[#E5E7EB] rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/40 disabled:bg-[#FAFBFC]"
              />
              <button
                onClick={() => onSave({ name: saveName.trim() || undefined })}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#07E5D1] hover:brightness-95 disabled:opacity-70 rounded-lg px-3 py-1.5"
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {isSaving ? 'Creating…' : 'Create survey'}
              </button>
              {!isSaving && (
                <button onClick={() => setShowSaveBar(false)} className="text-xs text-gray-400 hover:text-gray-600 px-2">
                  Cancel
                </button>
              )}
            </div>
            {isSaving && (
              <p className="text-[11px] text-[#07E5D1] flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" />
                The AI is structuring your questions into a survey — this can take up to a minute.
              </p>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowSaveBar(true)}
            disabled={!draftMarkdown.trim()}
            className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium text-white bg-[#07E5D1] hover:brightness-95 disabled:opacity-50 rounded-lg px-3 py-2"
          >
            <Save className="w-4 h-4" /> Save as survey
          </button>
        )}
      </div>
    </div>
  );
};

export default SurveyCanvas;
